import $ from "jquery";
import "slick-slider";

/**
 * Check if variable is a string and that it doesn't
 * contain only whitespace characters
 * @param {unknown} maybeString
 * @returns {boolean}
 */
function hasContent(maybeString) {
  if (typeof maybeString !== "string") return false;

  return maybeString.trim().length !== 0;
}

function removeTitleOverlayOnVideoStart(mutationsList, observer) {
  for (const mutation of mutationsList) {
    if (
      mutation.type === "attributes" &&
      mutation.attributeName === "class" &&
      mutation.target.className.includes("video-started")
    ) {
      const slider = mutation.target.closest(".story-builder__slider");
      const title = slider.querySelector(
        ".story-builder-slider__caption-container .story-builder-slider__title",
      );
      title.classList.add("story-builder-slider__title--no-overlay");
    }
  }
}

const observerConfig = { attributes: true };
const observer = new MutationObserver(removeTitleOverlayOnVideoStart);

/**
 * Check if slide has a video. If so, observe if video
 * starts by checking if "background-video--video-started" or
 * "youtube-video--video-started" classes are added, and then
 * add .story-builder-caption__title--no-overlay to description title
 * @param {HTMLElement} slideItem
 */
function observeSlide(slideItem) {
  const slideType = getSlideType(slideItem);

  if (slideType === "video") {
    const backgroundVideoEl = slideItem.querySelector(".background-video");
    observer.observe(backgroundVideoEl, observerConfig);
  }

  if (slideType === "youtube-video") {
    const youtubeVideoEl = slideItem.querySelector(".youtube-video");
    observer.observe(youtubeVideoEl, observerConfig);
  }
}

function getVideoType(slideItem) {
  if (slideItem.firstElementChild.classList.contains("youtube-video-mount"))
    return "youtube-video";
  else return "video";
}

/**
 * Slide types: video, youtube-video, image, split, rich-text, quote, embed, image-frame, split-quote-text, split-quote-image
 */
function getSlideType(slideItem) {
  const classList = slideItem.classList;
  if (classList.contains("story-builder-slide__item--video")) {
    return getVideoType(slideItem);
  } else if (classList.contains("story-builder-slide__item--image")) {
    return "image";
  } else if (classList.contains("story-builder-slide__item--split")) {
    if (slideItem.querySelector(".js-image-frame-slide")) {
      return "image-frame";
    } else if (slideItem.querySelector(".js-split-quote-text")) {
      return "split-quote-text";
    } else if (slideItem.querySelector(".js-split-quote-image")) {
      return "split-quote-image";
    } else {
      return "split";
    }
  } else if (classList.contains("story-builder-slide__item--rich-text")) {
    return "rich-text";
  } else if (classList.contains("story-builder-slide__item--quote")) {
    return "quote";
  } else if (classList.contains("story-builder-slide__item--embed")) {
    return "embed";
  } else {
    console.warn(
      `Couldn't detect slide type from className: "${slideItem.className}"`,
    );
    return "";
  }
}

function getSlideDescription(slideItem) {
  const slideType = getSlideType(slideItem);
  let title, caption, credit;

  switch (slideType) {
    case "video":
      title = slideItem.querySelector(".background-video__title")?.textContent;
      caption = slideItem.querySelector(
        ".background-video__caption",
      )?.textContent;
      credit = slideItem.querySelector(
        ".background-video__credit",
      )?.textContent;
      break;
    case "youtube-video":
      title = slideItem.querySelector(".youtube-video__title")?.textContent;
      caption = slideItem.querySelector(
        ".youtube-video__description",
      )?.textContent;
      credit = slideItem.querySelector(".youtube-video__credit")?.textContent;
      break;
    case "image":
      title = slideItem.querySelector(".image-block__title")?.textContent;
      caption = slideItem.querySelector(
        ".image-block__description",
      )?.textContent;
      credit = slideItem.querySelector(".image-block__source")?.textContent;
      break;
    case "split":
      caption = slideItem.querySelector(
        ".split-slide__description",
      )?.textContent;
      credit = slideItem.querySelector(".split-slide__credit")?.textContent;
      break;
    case "image-frame":
    case "split-quote-image":
    case "split-quote-text":
      caption = slideItem.querySelector(
        ".js-story-builder-caption__description",
      )?.textContent;
      credit = slideItem.querySelector(
        ".js-story-builder-caption__credit",
      )?.textContent;
      break;
    default:
      break;
  }

  return { title, caption, credit };
}

function setSlideDescription(root, slideItem) {
  const storyBuilderCaption = root.querySelector(
    ".story-builder-slider__caption-container",
  );

  const titleEl = storyBuilderCaption.querySelector(
    ".story-builder-slider__title",
  );

  const captionEl = storyBuilderCaption.querySelector(
    ".story-builder-slider__caption",
  );
  const creditEl = storyBuilderCaption.querySelector(
    ".story-builder-slider__credit",
  );

  // Clear previous title modifier
  titleEl.classList.remove("story-builder-slider__title--no-overlay");

  // Get slide theme and set title color
  if (
    slideItem
      .closest(".story-builder-slide")
      ?.classList.contains("story-builder-slide--light")
  ) {
    root.classList.add("story-builder--light");
  } else {
    root.classList.remove("story-builder--light");
  }

  const { title, caption, credit } = getSlideDescription(slideItem);

  // Determine if title should be overlaid over video
  const slideType = getSlideType(slideItem);
  if (slideType === "video") {
    const backgroundVideoEl = slideItem.querySelector(".background-video");
    if (backgroundVideoEl.className.includes("video-started")) {
      titleEl.classList.add("story-builder-slider__title--no-overlay");
    }
  }

  if (slideType === "youtube-video") {
    const youtubeVideoEl = slideItem.querySelector(".youtube-video");
    if (youtubeVideoEl.className.includes("video-started")) {
      titleEl.classList.add("story-builder-slider__title--no-overlay");
    }
  }

  if (hasContent(title)) {
    titleEl.textContent = title;
    titleEl.classList.remove("is-empty");
  } else {
    titleEl.textContent = "";
    titleEl.classList.add("is-empty");
  }

  const innerContainer = root.querySelector(
    ".story-builder-slider__caption-inner",
  );
  if (hasContent(caption) || hasContent(credit)) {
    innerContainer.classList.remove("is-empty");
  } else {
    innerContainer.classList.add("is-empty");
  }

  captionEl.textContent = caption ?? "";

  if (hasContent(credit)) {
    creditEl.textContent = credit;
    creditEl.classList.remove("is-empty");
  } else {
    creditEl.textContent = "";
    creditEl.classList.add("is-empty");
  }
}

const defaultConfig = {
  dots: false,
  infinite: false,
  adaptiveHeight: false,
  speed: 850,
  slidesToShow: 1,
  slidesToScroll: 1,
  accessibility: true,
  autoplay: false,
};

const initSlider = (el) => {
  const root = el.closest(".js-story-builder");
  const $el = $(el);
  const $slider = $el.find(".js-slide-container");
  const $slideNextButton = $el.find(".js-slide-next-button");
  const $slidePrevButton = $el.find(".js-slide-prev-button");
  const $slides = $el.find(".js-slide");
  const storyBuilderCaptionContainer = el.querySelector(
    ".story-builder-slider__caption-container",
  );

  $slider.show();

  const config = {
    nextArrow: $slideNextButton,
    prevArrow: $slidePrevButton,
    ...defaultConfig,
  };

  // set the total count
  $el.find(".js-slide-total").text($slides.length);

  $slider.on("init", function (event, slick) {
    for (const slide of slick.$slides) {
      const slideItem = slide.querySelector(".story-builder-slide__item");
      observeSlide(slideItem);
    }
  });

  $slider.slick(config);

  //removing inline styles added by Slick Slider
  $(".js-slide").removeAttr("style");

  $slider.on("beforeChange", function (event, slick, currentSlide, nextSlide) {
    storyBuilderCaptionContainer.classList.add(
      "story-builder-slider__caption-container--hide",
    );

    const $audio = $el.find(".slick-active .audio-is-playing");
    const $audioButton = $el.find(".slick-active .js-audio-player-button");

    $el.find(".js-slide-current").text(nextSlide + 1);

    if ($audio.length > 0) {
      $audioButton.click();
    }
  });

  $slider.on("afterChange", function (event, slick, currentSlide) {
    const activeSlideItem = el.querySelector(
      ".slick-active .story-builder-slide__item",
    );

    setSlideDescription(root, activeSlideItem);
    storyBuilderCaptionContainer.classList.remove(
      "story-builder-slider__caption-container--hide",
    );
  });
};

function addSlideTitleClass(slide) {
  const slideItem = slide.querySelector(".js-slide-item");
  const slideType = getSlideType(slideItem);
  let hasTitle;

  switch (slideType) {
    case "image":
      hasTitle = Boolean(slideItem.querySelector(".image-block__title"));
      break;
    case "video":
      hasTitle = Boolean(slideItem.querySelector(".background-video__title"));
      break;
    case "youtube-video":
      hasTitle = Boolean(slideItem.querySelector(".youtube-video__title"));
      break;
    default:
      hasTitle = false;
      break;
  }

  if (!hasTitle) return;

  slide.classList.add("story-builder-slide--has-title");
}

export default function initStoryBuilder() {
  document.querySelectorAll(".js-story-builder").forEach((el, i) => {
    const EE = el.closest(".is-page-editor") ? true : false;

    if (EE) {
      return;
    }

    const slider = el.querySelector(".js-story-builder-slider");
    const slides = slider.querySelectorAll(".js-slide");
    const firstSlideItem = slides[0].querySelector(".js-slide-item");

    // Add story-builder-slide--has-title modifier class to slides with titles
    for (const slide of slides) {
      addSlideTitleClass(slide);
    }

    // Set initial slide description
    setSlideDescription(el, firstSlideItem);

    initSlider(slider);
  });
}
